import {
    useTheme,
    VideoBackgroundBlurEffect,
    VideoBackgroundImage,
    VideoBackgroundNoEffect,
    VideoBackgroundReplacementEffect,
} from "@azure/communication-react";
import { useCallAdapter } from "../../provider";
import React, { useCallback, useMemo } from "react";
import { VideoEffectsItemProps } from "./VideoEffectsItem";
import { VideoBackgroundEffectsPicker, VideoBackgroundEffectsPickerStyles } from "./VideoEffectsBackgroundPicker";
import { Label, mergeStyles } from "@fluentui/react";
import { dropDownStyles } from "../../styles";
import { useCallAdapterSelector } from "../../hooks";
import { activeVideoBackgroundEffectSelector } from "../../selectors/activeVideoBackgroundEffectSelectors";

interface SelectVideoEffectPaneProps {
    renderTitle?: React.ReactNode;
}

export const SelectVideoEffectPane = (props: SelectVideoEffectPaneProps) => {
    const theme = useTheme();
    const callAdapter = useCallAdapter();
    const selectedEffect = useCallAdapterSelector(activeVideoBackgroundEffectSelector);

    const selectableVideoEffects: VideoEffectsItemProps[] = useMemo(() => {
        const videoEffects: VideoEffectsItemProps[] = [
            {
                itemKey: 'none',
                iconProps: {
                    iconName: 'RemoveVideoBackgroundEffect'
                },
                title: "Keine",
                tooltipProps: {
                    content: "Effekt entfernen"
                }
            },
            {
                itemKey: 'blur',
                iconProps: {
                    iconName: 'BlurVideoBackground'
                },
                title: "Blur",
                tooltipProps: {
                    content: "Hintergrund verwischen"
                }
            }
        ];
        const videoEffectImages = callAdapter.getState().videoBackgroundImages;

        if (videoEffectImages) {
            videoEffectImages.forEach((img: VideoBackgroundImage) => {
                videoEffects.push({
                    itemKey: img.key,
                    backgroundProps: {
                        url: img.url
                    },
                    tooltipProps: {
                        content: img.tooltipText ?? ''
                    }
                });
            });
        }
        return videoEffects;
    }, [callAdapter]);

    const onEffectChange = useCallback(
        async (effectKey: string) => {
            if (effectKey === 'blur') {
                const blurEffect: VideoBackgroundBlurEffect = {
                    effectName: effectKey
                };
                await callAdapter.startVideoBackgroundEffect(blurEffect);
                callAdapter.updateSelectedVideoBackgroundEffect(blurEffect);
            } else if (effectKey === 'none') {
                const noneEffect: VideoBackgroundNoEffect = {
                    effectName: effectKey
                };
                await callAdapter.stopVideoBackgroundEffects();
                callAdapter.updateSelectedVideoBackgroundEffect(noneEffect);
            } else {
                const backgroundImg = selectableVideoEffects.find((effect) => effect.itemKey === effectKey);
                if (backgroundImg && backgroundImg.backgroundProps) {
                    const replaceEffect: VideoBackgroundReplacementEffect = {
                        effectName: 'replacement',
                        key: effectKey,
                        backgroundImageUrl: backgroundImg.backgroundProps.url
                    };
                    await callAdapter.startVideoBackgroundEffect(replaceEffect);
                    callAdapter.updateSelectedVideoBackgroundEffect(replaceEffect);
                }
            }
        },
        [callAdapter, selectableVideoEffects]
    );

    return (
        <>
            {props.renderTitle ?? (
                <Label
                    id={'call-composite-local-camera-settings-label'}
                    className={mergeStyles(dropDownStyles(theme).label)}
                >
                    Effekte
                </Label>
            )}
            <VideoBackgroundEffectsPicker
                // label={"Hintergrund"}
                styles={backgroundPickerStyles}
                options={selectableVideoEffects ?? []}
                onChange={onEffectChange}
                selectedEffectKey={selectedEffect}
                itemsPerRow={3}
            />
        </>
    );
}

const backgroundPickerStyles: VideoBackgroundEffectsPickerStyles = {
    label: {
        fontSize: '0.75rem',
        lineHeight: '0.5rem',
        fontWeight: '400'
    },
    rowRoot: {
        justifyContent: 'center',
    },
};
